<script lang="ts">

export default {
  name: 'BenchmarkingTable',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import AccountBenchmarkingTableRow from '@/views/account/benchmarking/AccountBenchmarkingTableRow.vue';
import FilterBar from '@/components/FilterBar.vue';
import { Column } from './Benchmarking';
import { Benchmark } from '@/types/Benchmark';
import { accountService } from '@/services/api';
import { DownloadTableToCSV } from '@/utilities/Download';
import { DateTime } from 'luxon';
import { useRoute } from 'vue-router';
import Toggle from '@/components/v2/Toggle.vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { CalendarDaysIcon } from 'lucide-vue-next';

const props = withDefaults(defineProps<{
  titleText: string;
  columns: Column[];
  loading: boolean;
  accountData: Benchmark[];
  otherData: Benchmark[];
  averageData: Benchmark[];
  medianData: Benchmark[];
  primaryPolicyEffectiveDate?: string;
}>(), {
  primaryPolicyEffectiveDate: DateTime.now().toString(),
});

const emit = defineEmits<{
  'needs-update': []
}>();

const $route = useRoute();
const showVisuals = ref(false);
const countOfData = computed(() => props.otherData.length);

async function downloadTable() {
  const account = await accountService.findById($route.params.id as string);
  DownloadTableToCSV('#benchmarking-data', account.accountName + `-${new Date().toISOString().split('T')[0]}`);
}
</script>

<template>
  <div class="dashboard">
    <div>
      <FilterBar class="filter-bar">
        <slot name="filterbar"/>
      </FilterBar>

      <div v-if="loading" class="tw:space-y-2 tw:p-2">
        <Skeleton
          v-for="i in 8"
          :key="i"
          shape="rectangle"
          class="tw:w-full"
          height="44px" />
      </div>

      <div class="inner" v-if="!loading">
        <div class="heading">
          <h2>{{ titleText }}</h2>
          <div class="tw:flex tw:items-center" :class="!showVisuals ? '' : 'tw:invisible'">
            <download-button @click="downloadTable" />
            <div class="tw:ml-4 tw:badge tw:badge-primary tw:badge-outline tw:uppercase">{{ countOfData }} Peer Programs</div>
          </div>
          <toggle v-model="showVisuals" label="Show Visuals" id="show-visuals"/>
        </div>
        <slot name="charts" v-if="showVisuals"/>

        <div class="table-hscroller">
          <table v-if="!showVisuals" v-tablesort id="benchmarking-data" class="apptable sortable dense mt-4">
            <thead>
            <tr>
              <td v-for="column in columns" :key="column.fieldName"
                :data-test="column.fieldName" :class="`${column.type} ${column.classes || ''}`"
                :title="`${column.title}${column.annualized ? ' (Annualized)' : ''}`"
              >
                {{ column.title }}
                <CalendarDaysIcon v-if="column.annualized" :size="16" class="tw:inline tw:mb-0.5"/>
              </td>
            </tr>
            </thead>

            <tbody>
            <AccountBenchmarkingTableRow v-for="d in accountData" :key="`acc-${d.policyYear}-${d.accountId}`"
                                         highlight frozen
                                         :data="d" :columns="columns" @needs-update="emit('needs-update')" />

            <tr class="rule notcsv frozen tw:h-0.5"><td :colspan="columns.length"></td></tr>

            <AccountBenchmarkingTableRow :static="true" v-for="d in averageData" :key="d.key" :data="d" :columns="columns" highlight shaded frozen/>
            <AccountBenchmarkingTableRow :static="true" v-for="d in medianData" :key="d.key" :data="d" :columns="columns" highlight shaded frozen/>

            <tr class="rule notcsv frozen tw:h-0.5"><td :colspan="columns.length"></td></tr>

            <AccountBenchmarkingTableRow v-for="d in otherData" :key="`oth-${d.policyYear}-${d.accountId}`" :data="d" :columns="columns" @needs-update="$emit('needs-update')"/>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.heading {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: 1rem;
  align-content: center;
  align-items: center;
}
</style>
